import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PostcodeChecker from "../../components/feature/PostcodeChecker";
import ContactSection from "../home/components/ContactSection";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mightycleaningservices.co.uk";

const contactJsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": `${siteUrl}/contact#webpage`,
  "url": `${siteUrl}/contact`,
  "name": "Contact Mighty Cleaning Services Ltd | Free Quote Northfleet & Gravesend",
  "description":
    "Contact Mighty Cleaning Services Ltd for a free, no-obligation quote. Window cleaning, gutter clearance and exterior cleaning in Northfleet, Gravesend and Kent. We respond within 24 hours.",
  "inLanguage": "en-GB",
  "isPartOf": {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    "name": "Mighty Cleaning Services Ltd",
    "url": siteUrl,
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
      { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${siteUrl}/contact` },
    ],
  },
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "Mighty Cleaning Services Ltd",
    "telephone": "02084322244",
    "email": "info@mightycleaningservices.co.uk",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Suite 2007, Fleet House, Springhead Enterprise Park, Springhead Rd",
      "addressLocality": "Northfleet",
      "addressRegion": "Kent",
      "postalCode": "DA11 9HJ",
      "addressCountry": "GB",
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00",
      },
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "02084322244",
      "contactType": "customer service",
      "areaServed": "GB",
      "availableLanguage": "English",
    },
  },
};

export default function ContactPage() {
  return (
    <div className="font-body">
      <SEOHead
        title="Contact Mighty Cleaning Services Ltd | Free Quote Northfleet & Gravesend"
        description="Get a free, no-obligation quote for window cleaning, gutter clearance and exterior cleaning in Northfleet, Gravesend and Kent. Call 0208 43 222 44 — we respond within 24 hours."
        keywords="contact window cleaners Northfleet, free quote gutter clearance Gravesend, window cleaning quote Kent, book window cleaning Northfleet"
        canonical={`${siteUrl}/contact`}
      />
      <JsonLd id="contact-webpage" data={contactJsonLd} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/50c5d0257107e34b3141bd707af666e3.jpg"
              alt="Contact Mighty Cleaning Services Northfleet Kent"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2040]/90 via-[#0F2040]/75 to-[#0F2040]/40"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
            <span className="text-[#60AEFF] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
              Get In Touch
            </span>
            <h1
              className="font-heading text-white leading-tight mb-4 max-w-2xl"
              style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}
            >
              Request a Free Quote Today
            </h1>
            <p className="text-blue-100 font-body text-lg leading-relaxed max-w-xl mb-8">
              Covering <strong className="text-white">Northfleet, Gravesend, Kent</strong> and surrounding areas. We respond within 24 hours — no obligation.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-us"
                className="flex items-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Fill in the Form
              </a>
              <a
                href="tel:02084322244"
                className="flex items-center gap-2 border border-white/40 text-white hover:bg-white/10 font-heading font-semibold text-sm px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-[#60AEFF]"></i>
                0208 43 222 44
              </a>
            </div>
          </div>
        </section>

        {/* Quick info strip */}
        <section className="bg-[#1A3A6B] py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: "ri-phone-fill", label: "Call Us", value: "0208 43 222 44", href: "tel:02084322244" },
                { icon: "ri-map-pin-2-fill", label: "Based In", value: "Northfleet, Kent", href: undefined },
                { icon: "ri-time-fill", label: "Opening Hours", value: "Mon\u2013Fri: 9am \u2013 5pm", href: undefined },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-[#60AEFF] text-lg`}></i>
                  </div>
                  <div>
                    <span className="text-blue-300 text-xs font-body block">{item.label}</span>
                    {item.href ? (
                      <a href={item.href} className="text-white font-heading font-semibold text-sm hover:text-[#60AEFF] transition-colors cursor-pointer">
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-white font-heading font-semibold text-sm">{item.value}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Postcode checker */}
        <PostcodeChecker />

        {/* Enquiry form + map */}
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}
